import { type ReactNode, useState } from 'react'

import { Checkbox } from '@/components/ui/checkbox.tsx'
import { Input } from '@/components/ui/input.tsx'
import { Label } from '@/components/ui/label.tsx'

import { Button } from '@/components/ui/button.tsx'
import { Separator } from '@/components/ui/separator.tsx'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { cn } from '@/lib/utils.ts'
import { useMutateProject } from '@/requests/mutations/useMutateProject.ts'
import { useQueryProjectMembers, useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { getTimeZoneOffset, toISODate } from '@/utils/date.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'
import { capitalizeFirstLetter } from '@/utils/string.ts'

const sectionHeaderStyles = 'font-EN-KR-heading3-bold text-contents-primary'

export const ProjectSettingsPage = () => {
  const { projectMeta } = useQueryProjectMeta()
  const [projectName, setProjectName] = useState(projectMeta?.name ?? '')
  const { rename } = useMutateProject()
  const { user } = useUserContext()
  const { members } = useQueryProjectMembers()

  if (!projectMeta || !user) {
    return <></>
  }

  const userProjectMembership = members.find((m) => m.id === user.id.toString())

  const onSubmit = () => {
    const newTitle = projectName.trim()
    if (newTitle.length === 0) {
      return
    }

    if (newTitle === projectMeta.name) {
      return
    }
    rename(projectMeta.id, newTitle)
  }

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-[178px_1fr] h-full">
        <div className="bg-tb-navy-700 py-8 px-6">
          <p className="font-EN-KR-body1-bold text-contents-onDarkPrimary">Project Settings</p>
        </div>
        <div className="px-12 py-8 flex flex-col gap-8">
          <div>
            <h3 className={sectionHeaderStyles}>General</h3>
            <Separator className="my-4 bg-border-default-secondary" />
            <div className="grid grid-cols-[172px_1fr] gap-x-4 gap-y-2 items-center">
              <Title
                className={cn({
                  'self-start': userProjectMembership?.role === 'ADMIN',
                })}
              >
                Project Title
              </Title>
              {userProjectMembership?.role === 'ADMIN' ? (
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 justify-center">
                    <Input
                      value={projectName}
                      placeholder="Enter a name for your project"
                      onChange={(e) => {
                        if (e.target.value.length > 40) {
                          return
                        }
                        setProjectName(e.target.value)
                      }}
                      onKeyDown={(e) => onEnterInclusive(e, onSubmit)}
                      disabled={userProjectMembership?.role !== 'ADMIN'}
                      className="min-w-[311px] h-8 p-2 bg-surface-backgroundElevated font-EN-KR-body2-regular rounded-xs text-contents-primary"
                    />
                    <p className="text-contents-tertiary font-EN-KR-caption-regular">
                      The project name can be up to 40 characters
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    className="py-2 px-1.5 h-8 w-12 font-EN-KR-body2-bold rounded-xs"
                    onClick={onSubmit}
                    disabled={projectName.trim().length === 0}
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <ReadOnlyValue value={projectMeta.name} />
              )}
            </div>
          </div>
          <div>
            <h3 className={sectionHeaderStyles}>Privacy</h3>
            <Separator className="my-4 bg-border-default-secondary" />
            <div className="grid grid-cols-[172px_1fr] gap-x-4 gap-y-2 items-center">
              <Title>Project Privacy</Title>
              <div className="flex gap-1 items-center">
                <Checkbox variant="custom" checked={true} disabled={true} />
                <div className="flex gap-1">
                  <Label className="font-EN-KR-body2-bold text-contents-primary">Private</Label>
                  <p className="font-EN-KR-body2-regular text-contents-primary">
                    Accessible by people invited to the project
                  </p>
                </div>
              </div>
              <Title>Tracker Boot Agreement (Terms of Service)</Title>
              <PolicyLink
                href="/policies/trackerboot-agreement"
                display="https://trackerboot.com/policies/trackerboot-agreement"
              />
              <Title>Privacy Policy</Title>
              <PolicyLink display="https://trackerboot.com/policies/privacy-policy" href="/policies/privacy-policy" />

              <Title>Cookie Policy</Title>
              <PolicyLink display="https://trackerboot.com/policies/cookie-policy" href="/policies/cookie-policy" />

              <Title>Terms of Use</Title>
              <PolicyLink display="https://trackerboot.com/policies/terms-of-use" href="/policies/terms-of-use" />

              <Title>Data Processing Addendum</Title>
              <PolicyLink
                display="https://trackerboot.com/policies/data-processing-addendum"
                href="/policies/data-processing-addendum"
              />
              <Title>Sub-processors</Title>
              <PolicyLink display="https://trackerboot.com/legal/subprocessors" href="/legal/subprocessors" />

              <Title>Security Measures</Title>
              <PolicyLink display="https://trackerboot.com/legal/security-measures" href="/legal/security-measures" />
            </div>
          </div>

          <div>
            <h3 className={sectionHeaderStyles}>Iterations and Velocity</h3>
            <Separator className="my-4 bg-border-default-secondary" />
            <div className="grid grid-cols-[172px_1fr] gap-x-4 gap-y-2 items-center">
              <Title>Start Iterations On</Title>
              <ReadOnlyValue value={capitalizeFirstLetter(projectMeta.weekStartDay)} />
              <Title>Project Start Date</Title>
              <ReadOnlyValue value={toISODate(projectMeta.createdAt)} />
              <Title>Project Time Zone</Title>
              <ReadOnlyValue value={`(${getTimeZoneOffset(projectMeta.timeZone)}) ${projectMeta.timeZone}`} />
              <Title>Iteration Length</Title>
              <ReadOnlyValue value={`${projectMeta.iterationLength} week`} />
              <Title>Initial Velocity</Title>
              <ReadOnlyValue value={projectMeta.initialVelocity} />
              <Title>Velocity Strategy</Title>
              <ReadOnlyValue value="Average of 3 iterations" />
              <Title>Show Completed Iterations</Title>
              <ReadOnlyValue value={4} />
              <Title>Auto Iteration Plan</Title>
              <div className="flex gap-1">
                <Checkbox
                  variant="custom"
                  checked={projectMeta.automaticPlanning}
                  aria-label="auto-planning"
                  disabled={true}
                />
                <Label className="font-EN-KR-body2-regular text-contents-primary">
                  Stories move to/from current iteration based on project velocity
                </Label>
              </div>
            </div>
          </div>
          <div>
            <h3 className={sectionHeaderStyles}>Access</h3>
            <Separator className="my-4 bg-border-default-secondary" />
            <div className="grid grid-cols-[172px_1fr] gap-x-4 gap-y-2 items-center">
              <Title>Project ID</Title>
              <span className="font-EN-KR-body2-regular text-contents-primary">{projectMeta.id}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Title = ({ className, children }: { className?: string; children: ReactNode }) => {
  return (
    <p className={cn('h-8 place-content-center font-EN-KR-body2-bold text-contents-tertiary', className)}>{children}</p>
  )
}

const ReadOnlyValue = ({ value }: { value: string | number }) => {
  return <p className="font-EN-KR-body2-regular text-contents-primary">{value}</p>
}

const PolicyLink = ({ display, href }: { display: string; href: string }) => {
  return (
    <div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-EN-KR-body2-regular text-contents-primary underline"
      >
        {display}
      </a>
    </div>
  )
}
